import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@douyinfe/semi-ui';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ClaudeLogo, OpenAILogo, GoogleLogo, DeepSeekLogo, MistralLogo } from '../../components/icons/AILogos';
import { WyzaiLogo } from '../../components/icons/Logo';

gsap.registerPlugin(useGSAP);

const Home = () => {
  const navigate = useNavigate();
  const homeRef = useRef(null);

  useGSAP(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const heroItems = ['.hero-badge', '.hero-title', '.hero-desc', '.hero-dev-notice', '.hero-actions', '.hero-ai-logos'];
    const cards = gsap.utils.toArray('.main-bento-grid > div');
    const bars = gsap.utils.toArray('.chart-bar');

    if (reduceMotion) {
      gsap.set(homeRef.current, { autoAlpha: 1, y: 0 });
      gsap.set(cards, { autoAlpha: 1, y: 0, scale: 1, rotateX: 0 });
      gsap.set(heroItems, { autoAlpha: 1, y: 0 });
      gsap.set(bars, { height: (index, target) => target.style.getPropertyValue('--height') });
      return;
    }

    gsap.set(homeRef.current, { autoAlpha: 1, y: 0 });
    gsap.set(cards, { autoAlpha: 0, y: 34, scale: 0.97, rotateX: -4, transformOrigin: '50% 100%' });
    gsap.set(heroItems, { autoAlpha: 0, y: 18 });
    gsap.set(bars, { height: 0 });

    const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

    timeline
      .to(cards, { autoAlpha: 1, y: 0, scale: 1, rotateX: 0, duration: 0.72, stagger: 0.055 })
      .to(heroItems, { autoAlpha: 1, y: 0, duration: 0.58, stagger: 0.06 }, 0.1)
      .fromTo('.gradient-text', { backgroundPosition: '0% 50%' }, { backgroundPosition: '100% 50%', duration: 1.35, ease: 'sine.inOut' }, 0.18)
      .to(bars, { height: (index, target) => target.style.getPropertyValue('--height'), duration: 0.8, stagger: 0.035, ease: 'power2.out' }, 0.48)
      .fromTo('.ai-logo-item, .ai-logo-count', { autoAlpha: 0, y: 10, scale: 0.82 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, stagger: 0.05, ease: 'back.out(1.8)' }, 0.62);
  }, { scope: homeRef });

  return (
    <div ref={homeRef} className="home-page-new">
      {/* 全宽 Bento Grid 布局 */}
      <section className="main-bento-grid">
        {/* 左侧大卡片 - 英雄区域 */}
        <div className="bento-hero-card" style={{ '--delay': '0' }}>
          <div className="hero-badge">
            <WyzaiLogo size={20} />
            <span>wyzai 管理平台</span>
          </div>
          <h1 className="hero-title">
            智能 API 密钥
            <br />
            <span className="gradient-text">管理工具</span>
          </h1>
          <p className="hero-desc">
            专业的 wyzAPI 令牌管理平台，提供余额查询、转移、数据分析等全方位服务
          </p>
          <div className="hero-dev-notice">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>持续优化中 · 功能不断完善</span>
          </div>
          <div className="hero-actions">
            <Button className="hero-btn hero-btn--primary" size="large" onClick={() => navigate('/query')}>
              开始查询
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Button>
            <Button className="hero-btn hero-btn--secondary" size="large" onClick={() => navigate('/transfer')}>
              余额转移
            </Button>
          </div>
          {/* AI Logos */}
          <div className="hero-ai-logos">
            <span className="ai-logos-label">支持的模型</span>
            <div className="ai-logos-list">
              <div className="ai-logo-item"><ClaudeLogo size={18} /></div>
              <div className="ai-logo-item"><OpenAILogo size={18} /></div>
              <div className="ai-logo-item"><GoogleLogo size={18} /></div>
              <div className="ai-logo-item"><DeepSeekLogo size={18} /></div>
              <div className="ai-logo-item"><MistralLogo size={18} /></div>
              <div className="ai-logo-count">+15</div>
            </div>
          </div>
        </div>

        {/* 右上 - 余额卡片 */}
        <div className="bento-stat-card bento-stat--balance" style={{ '--delay': '1' }}>
          <div className="stat-label">BALANCE</div>
          <div className="stat-value-large">$125.50</div>
          <div className="stat-sub">余额总额</div>
          <div className="stat-trend stat-trend--up">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/>
              <polyline points="17,6 23,6 23,12"/>
            </svg>
            +12.5%
          </div>
        </div>

        {/* 右中 - 可用率 */}
        <div className="bento-stat-card bento-stat--uptime" style={{ '--delay': '2' }}>
          <div className="stat-label">UPTIME</div>
          <div className="stat-value-large">99.9<span className="stat-unit">%</span></div>
          <div className="stat-sub">服务可用率</div>
        </div>

        {/* 右下 - 调用次数 */}
        <div className="bento-stat-card bento-stat--calls" style={{ '--delay': '3' }}>
          <div className="stat-label">REQUESTS</div>
          <div className="stat-value-large">1,234</div>
          <div className="stat-sub">总调用次数</div>
        </div>

        {/* 功能卡片 - 令牌查询 */}
        <div className="bento-feature-card" style={{ '--delay': '4' }} onClick={() => navigate('/query')}>
          <div className="feature-icon-box">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
          </div>
          <div className="feature-content">
            <h3>令牌查询</h3>
            <p>实时查询余额、使用情况</p>
          </div>
          <div className="feature-arrow">→</div>
        </div>

        {/* 功能卡片 - 余额转移 */}
        <div className="bento-feature-card" style={{ '--delay': '5' }} onClick={() => navigate('/transfer')}>
          <div className="feature-icon-box">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 1l4 4-4 4"/>
              <path d="M3 11V9a4 4 0 014-4h14"/>
              <path d="M7 23l-4-4 4-4"/>
              <path d="M21 13v2a4 4 0 01-4 4H3"/>
            </svg>
          </div>
          <div className="feature-content">
            <h3>余额转移</h3>
            <p>安全便捷转移额度</p>
          </div>
          <div className="feature-arrow">→</div>
        </div>

        {/* 功能卡片 - 文档 */}
        <div className="bento-feature-card" style={{ '--delay': '6' }} onClick={() => navigate('/claude-docs')}>
          <div className="feature-icon-box">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
            </svg>
          </div>
          <div className="feature-content">
            <h3>使用文档</h3>
            <p>完整配置教程指南</p>
          </div>
          <div className="feature-arrow">→</div>
        </div>

        {/* 功能卡片 - 价格 */}
        <div className="bento-feature-card" style={{ '--delay': '7' }} onClick={() => navigate('/pricing')}>
          <div className="feature-icon-box">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
            </svg>
          </div>
          <div className="feature-content">
            <h3>模型价格</h3>
            <p>查看实时定价信息</p>
          </div>
          <div className="feature-arrow">→</div>
        </div>

        {/* 功能卡片 - 服务状态（新窗口） */}
        <div className="bento-feature-card" style={{ '--delay': '8' }} onClick={() => window.open('https://status.wyzlab.ai/', '_blank')}>
          <div className="feature-icon-box feature-icon-box--green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
          </div>
          <div className="feature-content">
            <h3>服务状态</h3>
            <p>实时监控运行状况</p>
          </div>
          <div className="feature-arrow">↗</div>
        </div>

        {/* 分析图表卡片 */}
        <div className="bento-analytics-card" style={{ '--delay': '8' }}>
          <div className="analytics-header">
            <div className="stat-label">ANALYTICS</div>
            <h3>追踪每一个 Token</h3>
          </div>
          <p className="analytics-desc">实时用量统计、模型成本分析、详细请求日志</p>
          <div className="analytics-chart">
            {[65, 85, 45, 70, 90, 55, 75, 80, 60, 95, 50, 85, 70, 88].map((h, i) => (
              <div key={i} className="chart-bar" style={{ '--height': `${h}%`, '--delay': `${i * 0.04}s` }} />
            ))}
          </div>
        </div>

        {/* 数据指标卡片 */}
        <div className="bento-metrics-card" style={{ '--delay': '9' }}>
          <div className="stat-label">PERFORMANCE</div>
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="metric-value">50<span className="metric-unit">ms</span></span>
              <span className="metric-label">P95 延迟</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">50+</span>
              <span className="metric-label">模型数量</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">10+</span>
              <span className="metric-label">服务商</span>
            </div>
          </div>
        </div>

        {/* Claude Code 功能卡片 - 全宽 */}
        <div className="bento-cta-card" style={{ '--delay': '10' }} onClick={() => navigate('/claude-docs')}>
          <div className="cta-content">
            <div className="cta-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
            </div>
            <div className="cta-text">
              <h3>Claude Code 配置指南</h3>
              <p>了解如何配置和使用智能编程助手</p>
            </div>
          </div>
          <div className="cta-btn">
            立即查看 →
          </div>
        </div>

      </section>
    </div>
  );
};

export default Home;
